/** @jsx jsx */
import React, { useEffect } from "react"
import { Box, useMediaQuery, BoxProps } from "@chakra-ui/react"
import {
  Transition,
  AnimationControls,
  TargetAndTransition,
  VariantLabels,
  Target,
} from "framer-motion"
import { Text, MotionBox } from "@src/shared/components"
import { css, jsx } from "@emotion/react"
import {
  useDiclosureContext,
  DisclosureContextInterface,
} from "@src/shared/hooks"

export interface HamburgerIconMenuProps extends BoxProps {
  /**
   *  Provides information to screen readers, if value is menu => (menu open | menu close) will be read
   */
  ariaLabel: string
  /**
   * Menu Label. Visible only on desktop
   */
  menuLabel: string
  /**
   * Menu Label color
   */
  menuLabelColor?: string
  /**
   * Hamburger color
   */
  hamburgerMenuColor?: string
  /**
   * Menu container CSS
   */
  menuContainerCSS?: BoxProps
  /**
   * Inital menu animation
   */
  menuInitialAnimationCSS?: {
    [key: string]: boolean | Target | VariantLabels
  }
  /**
   * Menu animation when menu is open
   */
  menuIsOpenAnimationCSS?: {
    [key: string]:
      | AnimationControls
      | TargetAndTransition
      | VariantLabels
      | boolean
      | string
      | number
  }
  /**
   * Menu animation when menu is close
   */
  menuIsCloseAnimationCSS?: {
    [key: string]:
      | AnimationControls
      | TargetAndTransition
      | VariantLabels
      | boolean
      | string
      | number
  }
  /**
   * Menu transition to control animation
   */
  menuTransitionCSS?: {
    [key: string]: Transition | string | number
  }
}

export const HamburgerIconMenu: React.FC<HamburgerIconMenuProps> =
  React.forwardRef(
    (
      {
        ariaLabel,
        menuLabel,
        menuLabelColor = "black",
        hamburgerMenuColor = "black",
        menuContainerCSS,
        menuIsCloseAnimationCSS,
        menuIsOpenAnimationCSS,
        menuTransitionCSS,
        menuInitialAnimationCSS,
        children,
      },
      ref: React.Ref<HTMLButtonElement>
    ) => {
      const { onToggle, isOpen } =
        useDiclosureContext() as DisclosureContextInterface
      const [isTablet] = useMediaQuery("(min-width: 768px)")

      useEffect(() => {
        const closeMenu = (e: KeyboardEvent) => {
          if (isOpen && e.code === "Escape") {
            onToggle()
          }
        }
        document.addEventListener("keydown", closeMenu)

        return () => {
          document.removeEventListener("keydown", closeMenu)
        }
      }, [isOpen, onToggle])

      // We use variants in order to animate the component,
      // based on the state of the hamburger button
      const firstHamburgerMenuBarVariant = {
        close: { y: 4, rotate: 0 },
        open: { rotate: 45, y: 0 },
      }
      const secondtHamburgerMenuBarVariant = {
        close: { y: -4, rotate: 0 },
        open: { rotate: -45, y: 0 },
      }

      const menuContainer = {
        close: { ...menuIsCloseAnimationCSS },
        open: { ...menuIsOpenAnimationCSS },
      }

      const labelVariant = {
        close: {
          x: 0,
          opacity: 1,
          display: "block",
        },
        open: {
          x: -5,
          opacity: 0,
          transitionEnd: {
            display: "none",
            x: 0,
          },
        },
      }

      return (
        <React.Fragment>
          <button
            ref={ref}
            css={css`
          display: inline-flex;
          align-items: center;
          padding: 0.8rem;
          position: relative;
          z-index: 12;
          }
        `}
            type="button"
            aria-label={`${ariaLabel} is ${isOpen ? "open" : "close"}`}
            aria-expanded={isOpen}
            title={`${ariaLabel} toggle`}
            onClick={() => onToggle()}
          >
            {isTablet ? (
              <Text
                pt={1}
                pr={4}
                fontWeight="bold"
                type="caption"
                animate={isOpen ? "open" : "close"}
                variants={labelVariant}
                display="none"
                color={menuLabelColor}
              >
                {menuLabel.toUpperCase()}
              </Text>
            ) : (
              ""
            )}
            <Box
              className="Hamburger"
              position="relative"
              width="1.5rem"
              height="1.5rem"
            >
              <MotionBox
                position="absolute"
                height="2px"
                width="100%"
                background={hamburgerMenuColor}
                transformOrigin="50% 50%"
                top="50%"
                initial={false}
                animate={isOpen ? "open" : "close"}
                variants={firstHamburgerMenuBarVariant}
              />
              <MotionBox
                position="absolute"
                height="2px"
                width="100%"
                initial={false}
                background={hamburgerMenuColor}
                transformOrigin="50% 50%"
                top="50%"
                animate={isOpen ? "open" : "close"}
                variants={secondtHamburgerMenuBarVariant}
              />
            </Box>
          </button>
          {menuIsOpenAnimationCSS && (
            <MotionBox
              data-testid="menu-container"
              animate={isOpen ? "open" : "close"}
              transition={menuTransitionCSS}
              variants={menuContainer}
              initial={menuInitialAnimationCSS}
              {...menuContainerCSS}
            >
              {children}
            </MotionBox>
          )}
        </React.Fragment>
      )
    }
  )
